import React, { useState, useEffect } from 'react';
import { useSuperAdminAuth } from '../../../contexts/SuperAdminAuthContext';
import { governanceAPI } from '../../../lib/governanceApi';
import { toast } from 'sonner';
import { Bot, Play, RefreshCw, AlertTriangle, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Skeleton } from '../../../components/ui/skeleton';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Checkbox } from '../../../components/ui/checkbox';
import { PageHeader, EmptyState } from '../design';

/**
 * Agents Page - Governance Panel
 * 
 * PHASE 2 FEATURES:
 * - Resume auto-paused agents
 * - Explicit confirmation required
 * - Full audit trail
 */
export default function Agents() {
  const { isAuthenticated } = useSuperAdminAuth();
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [resumeDialog, setResumeDialog] = useState({ open: false, agent: null });
  const [resumeReason, setResumeReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [resuming, setResuming] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      fetchAgents();
    }
  }, [isAuthenticated]);

  const fetchAgents = async () => {
    setLoading(true);
    try {
      const res = await governanceAPI.getAutoPausedAgents();
      const data = res.data;
      setAgents(data.auto_paused_agents || []);
    } catch (error) {
      console.error('[Agents] Failed to fetch:', error);
    } finally {
      setLoading(false);
    }
  };

  const openResumeDialog = (agent) => {
    setResumeDialog({ open: true, agent });
    setResumeReason('');
    setConfirmed(false);
  };

  const handleResume = async () => {
    if (!confirmed || resumeReason.length < 10) {
      toast.error('Please confirm and provide a reason (min 10 characters)');
      return;
    }

    setResuming(true);
    try {
      const agentName = resumeDialog.agent?.agent || resumeDialog.agent?.agent_name;
      const res = await governanceAPI.resumeAgent(agentName, {
        reason: resumeReason,
        confirmation: true
      });

      if (res.data?.resumed) {
        toast.success(`Agent "${agentName}" resumed successfully`);
        setResumeDialog({ open: false, agent: null });
        fetchAgents();
      } else {
        toast.error(res.data?.error || 'Failed to resume agent');
      }
    } catch (error) {
      console.error('[Agents] Resume failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to resume agent');
    } finally {
      setResuming(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="agents-loading">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="agents">
      <PageHeader
        title="Auto-Paused Agents"
        subtitle="Agents paused due to low confidence scores"
        actions={
          <Button variant="outline" size="sm" onClick={fetchAgents} data-testid="refresh-agents">
            <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
          </Button>
        }
      />

      {agents.length === 0 ? (
        <div className="gov-card p-8" data-testid="agents-empty">
          <EmptyState icon={CheckCircle} title="No auto-paused agents" hint="All agents are operating normally." />
        </div>
      ) : (
        <div className="space-y-4">
          {agents.map((a) => (
            <Card key={a.agent || a.agent_name} className="border-amber-200 bg-amber-50/50 dark:border-amber-900 dark:bg-amber-950/20">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-lg bg-amber-100 flex items-center justify-center">
                      <Bot className="h-6 w-6 text-amber-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">{a.agent || a.agent_name}</h3>
                        <Badge variant="secondary">paused</Badge>
                      </div>
                      {a.reason && (
                        <p className="text-sm text-muted-foreground">{a.reason}</p>
                      )}
                      <div className="flex gap-4 mt-1 text-xs text-muted-foreground">
                        {a.confidence_at_pause && (
                          <span>Confidence at pause: {(a.confidence_at_pause * 100).toFixed(1)}%</span>
                        )}
                        {a.paused_at && (
                          <span>Paused: {new Date(a.paused_at).toLocaleString()}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button 
                    variant="default"
                    onClick={() => openResumeDialog(a)}
                    data-testid={`resume-${a.agent || a.agent_name}`}
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Resume Agent
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Resume Confirmation Dialog */}
      <Dialog open={resumeDialog.open} onOpenChange={(open) => setResumeDialog({ ...resumeDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Resume Auto-Paused Agent
            </DialogTitle>
            <DialogDescription>
              You are about to resume agent "{resumeDialog.agent?.agent || resumeDialog.agent?.agent_name}".
              This agent was paused due to low confidence scores.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Agent Details */}
            <div className="rounded-lg border p-3 bg-slate-50 dark:bg-slate-900">
              <p className="text-sm"><strong>Agent:</strong> {resumeDialog.agent?.agent || resumeDialog.agent?.agent_name}</p>
              {resumeDialog.agent?.confidence_at_pause && (
                <p className="text-sm"><strong>Confidence at pause:</strong> {(resumeDialog.agent.confidence_at_pause * 100).toFixed(1)}%</p>
              )}
              {resumeDialog.agent?.paused_at && (
                <p className="text-sm"><strong>Paused at:</strong> {new Date(resumeDialog.agent.paused_at).toLocaleString()}</p>
              )}
            </div>

            {/* Reason */}
            <div>
              <Label htmlFor="resume-reason">
                Reason for Resume <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="resume-reason"
                placeholder="Explain why you are resuming this agent (min 10 characters)..."
                value={resumeReason}
                onChange={(e) => setResumeReason(e.target.value)}
                className="mt-2"
                data-testid="resume-reason"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {resumeReason.length}/10 characters minimum
              </p>
            </div>

            {/* Confirmation Checkbox */}
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="confirm-resume" 
                checked={confirmed}
                onCheckedChange={setConfirmed}
                data-testid="confirm-checkbox"
              />
              <Label htmlFor="confirm-resume" className="text-sm">
                I understand that resuming this agent will reset its confidence streak and allow it to execute AI operations again.
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setResumeDialog({ open: false, agent: null })}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleResume}
              disabled={!confirmed || resumeReason.length < 10 || resuming}
              data-testid="confirm-resume"
            >
              {resuming ? 'Resuming...' : 'Resume Agent'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
